import React, { useState, useEffect } from 'react';
import {
  Users,
  Activity,
  DollarSign,
  TrendingUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { firestoreService } from '../services/firestoreService';
import { apiService } from '../services/api';
import type { AdminStats, UsageSummaryResponse } from '../types';
import { formatCurrency } from '../utils';

export default function Dashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [usageSummary, setUsageSummary] = useState<UsageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsResult, usageResult] = await Promise.all([
          firestoreService.getStats(),
          apiService.getUsageSummary(30).catch(() => null),
        ]);

        setStats(statsResult);
        setUsageSummary(usageResult);
      } catch (err: any) {
        setError('Failed to load dashboard statistics');
        console.error('Dashboard error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      name: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      icon: Users,
      change: `${stats.recentLogins} today`,
      changeType: 'positive' as const,
      color: 'bg-blue-500'
    },
    {
      name: 'Active Users',
      value: stats.activeUsers.toLocaleString(),
      icon: CheckCircle,
      change: stats.suspendedUsers > 0 ? `${stats.suspendedUsers} suspended` : 'none suspended',
      changeType: stats.suspendedUsers > 0 ? 'negative' as const : 'positive' as const,
      color: 'bg-green-500'
    },
    {
      name: 'Monthly Revenue',
      value: formatCurrency(stats.monthlyRevenue),
      icon: DollarSign,
      change: formatCurrency(stats.totalRevenue) + ' total',
      changeType: 'positive' as const,
      color: 'bg-yellow-500'
    },
    {
      name: 'Pending Actions',
      value: stats.pendingActions.toString(),
      icon: AlertTriangle,
      change: stats.pendingActions === 0 ? 'all clear' : 'needs review',
      changeType: stats.pendingActions > 0 ? 'negative' as const : 'positive' as const,
      color: 'bg-red-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of Aegis AI user management and system statistics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6"
          >
            <dt>
              <div className={`absolute rounded-md p-3 ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p
                className={`ml-2 flex items-baseline text-sm font-semibold ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {stat.change}
              </p>
            </dd>
          </div>
        ))}
      </div>

      {/* Subscription Breakdown */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Subscription Breakdown
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(stats.subscriptionBreakdown).map(([tier, count]) => (
              <div key={tier} className="bg-gray-50 overflow-hidden rounded-lg px-4 py-5">
                <dt className="text-sm font-medium text-gray-500 capitalize">
                  {tier} Plan
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {count.toLocaleString()}
                </dd>
              </div>
            ))}
          </div>
        </div>
      </div>

      {usageSummary && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Managed API Usage (30 days)
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  First control-panel view for backend consumption and cloud cost.
                </p>
              </div>
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                {usageSummary.range.eventCount.toLocaleString()} events
              </span>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Requests</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{usageSummary.totalRequests.toLocaleString()}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Total Tokens</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{usageSummary.totalTokens.toLocaleString()}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Estimated Cost</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(usageSummary.totalCostUsd)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Success Rate</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{usageSummary.successRate}%</div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Top Models</h4>
                <div className="mt-3 space-y-2">
                  {usageSummary.byModel.slice(0, 5).map((item) => (
                    <div key={item.model} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
                      <span className="text-sm text-gray-700">{item.model}</span>
                      <span className="text-sm font-medium text-gray-900">{item.requests} req · {formatCurrency(item.costUsd)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Top Users</h4>
                <div className="mt-3 space-y-2">
                  {usageSummary.byUser.slice(0, 5).map((item) => (
                    <div key={item.userId} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
                      <span className="truncate text-sm text-gray-700">{item.email || item.userId}</span>
                      <span className="text-sm font-medium text-gray-900">{item.requests} req · {formatCurrency(item.costUsd)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Recent Activity
          </h3>
          <div className="mt-5">
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                          <Users className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            New user registered: <span className="font-medium text-gray-900">john.doe@example.com</span>
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime="2023-01-09T11:00">2 hours ago</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="relative pb-8">
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                          <TrendingUp className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            User upgraded to <span className="font-medium text-gray-900">Premium</span> plan
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime="2023-01-09T10:00">3 hours ago</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="relative">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center ring-8 ring-white">
                          <DollarSign className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            Refund processed for <span className="font-medium text-gray-900">$29.99</span>
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time dateTime="2023-01-09T09:00">4 hours ago</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 